import { useEffect, useState, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import '../index.css';

const GROUP_TYPES = {
  CLIENTS: 'Clients',
  SUPPLIERS: 'Suppliers',
};

const Groups = () => {
  const [groupType, setGroupType] = useState(GROUP_TYPES.CLIENTS);
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupData, setGroupData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGroupData, setIsLoadingGroupData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const PAGE_SIZE = 20;
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);

  // Cache for client data
  const clientDataCache = useRef({});

  // Fetch all unique groups from the database
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setIsLoading(true);
        const snapshot = await getDocs(collection(db, 'cloudtree-database'));
        const docs = snapshot.docs.map(doc => doc.data());
        let uniqueGroups;
        if (groupType === GROUP_TYPES.CLIENTS) {
          uniqueGroups = [...new Set(docs.map(doc => doc['Bill To']).filter(Boolean))];
        } else {
          uniqueGroups = [...new Set(docs.map(doc => doc['Purchased From']).filter(Boolean))];
        }
        const sortedGroups = uniqueGroups.sort((a, b) => a.localeCompare(b));
        setGroups(sortedGroups);
        setFilteredGroups(sortedGroups);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGroups();
  }, [groupType]);

  // Filter groups based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter(group =>
        group.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchTerm, groups]);

  // Fetch data for selected group with pagination
  const fetchGroupData = useCallback(async (groupName, page = 1, append = false) => {
    if (!groupName) return;
    try {
      setIsLoadingGroupData(true);
      const cacheKey = `${groupType}_${groupName}_${page}`;
      if (clientDataCache.current[cacheKey]) {
        setGroupData(prev => append ? [...prev, ...clientDataCache.current[cacheKey]] : clientDataCache.current[cacheKey]);
        setHasMoreData(clientDataCache.current[cacheKey].length === PAGE_SIZE);
        return;
      }
      let q;
      if (groupType === GROUP_TYPES.CLIENTS) {
        q = query(
          collection(db, 'cloudtree-database'),
          where('Bill To', '==', groupName),
          limit(PAGE_SIZE)
        );
      } else {
        q = query(
          collection(db, 'cloudtree-database'),
          where('Purchased From', '==', groupName),
          limit(PAGE_SIZE)
        );
      }
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sortedDocs = docs.sort((a, b) => {
        const dateA = a['Date'] || '';
        const dateB = b['Date'] || '';
        return dateB.localeCompare(dateA);
      });
      clientDataCache.current[cacheKey] = sortedDocs;
      setGroupData(prev => append ? [...prev, ...sortedDocs] : sortedDocs);
      setHasMoreData(sortedDocs.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching group data:', error);
    } finally {
      setIsLoadingGroupData(false);
    }
  }, [groupType]);

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setCurrentPage(1);
    fetchGroupData(group, 1, false);
  };

  const loadMoreData = () => {
    if (!selectedGroup || isLoadingGroupData || !hasMoreData) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchGroupData(selectedGroup, nextPage, true);
  };

  // Update allColumns whenever groupData changes
  useEffect(() => {
    if (groupData.length > 0) {
      const columnsSet = new Set();
      groupData.forEach(item => {
        Object.keys(item).forEach(key => columnsSet.add(key));
      });
      // Exclude 'id' and any column containing 'pdf' (case-insensitive)
      const columnsArr = Array.from(columnsSet)
        .filter(col => col.toLowerCase() !== 'id' && !col.toLowerCase().includes('pdf'))
        .sort((a, b) => a.localeCompare(b));
      setAllColumns(columnsArr);
      setSelectedColumns(columnsArr); // Default: all selected
    } else {
      setAllColumns([]);
      setSelectedColumns([]);
    }
  }, [groupData]);

  // Function to download group data as CSV with selected columns
  const downloadGroupCSVWithColumns = (columnsToInclude) => {
    if (!groupData.length) {
      alert("No data to download.");
      return;
    }
    if (!columnsToInclude.length) {
      alert("Please select at least one column.");
      return;
    }
    const header = columnsToInclude.join(',');
    const rows = groupData.map(item => {
      return columnsToInclude.map(col => {
        const value = item[col] || '';
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      }).join(',');
    });
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedGroup}_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Modal handlers
  const handleOpenCSVModal = () => setShowCSVModal(true);
  const handleCloseCSVModal = () => setShowCSVModal(false);
  const handleColumnCheckbox = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col)
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };
  const handleSelectAllColumns = () => setSelectedColumns(allColumns);
  const handleDeselectAllColumns = () => setSelectedColumns([]);
  const handleSubmitCSVModal = (e) => {
    e.preventDefault();
    downloadGroupCSVWithColumns(selectedColumns);
    setShowCSVModal(false);
  };

  return (
    <div className="groups-root-layout">
      {/* Top: Toggle bar */}
      <div className="groups-toggle-bar">
        <button
          className={`group-toggle-btn${groupType === GROUP_TYPES.CLIENTS ? ' selected' : ''}`}
          onClick={() => setGroupType(GROUP_TYPES.CLIENTS)}
        >
          Clients
        </button>
        <button
          className={`group-toggle-btn${groupType === GROUP_TYPES.SUPPLIERS ? ' selected' : ''}`}
          onClick={() => setGroupType(GROUP_TYPES.SUPPLIERS)}
        >
          Suppliers
        </button>
      </div>
      {/* Bottom: Main content */}
      <div className="groups-main-content">
        <div className="secret-pages clients-page">
          <div className="clients-container">
            {/* Left sidebar - Groups List */}
            <div className="clients-sidebar">
              <div className="clients-search">
                <input
                  type="text"
                  placeholder={`Search ${groupType.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="clients-search-input"
                />
              </div>
              <div className="clients-list">
                {isLoading ? (
                  <p className="loading-message">Loading {groupType.toLowerCase()}...</p>
                ) : (
                  filteredGroups.map((group, index) => (
                    <div
                      key={index}
                      className={`client-item ${selectedGroup === group ? 'selected' : ''}`}
                      onClick={() => handleGroupSelect(group)}
                    >
                      {group}
                    </div>
                  ))
                )}
              </div>
            </div>
            {/* Right content - Group Details */}
            <div className="client-details">
              {selectedGroup ? (
                <>
                  <div className="client-header-container">
                    <h2 className="client-header">{selectedGroup}</h2>
                    <button 
                      className="download-csv-btn"
                      onClick={handleOpenCSVModal}
                      disabled={isLoadingGroupData || groupData.length === 0}
                    >
                      Download CSV
                    </button>
                  </div>
                  {isLoadingGroupData && groupData.length === 0 ? (
                    <p className="loading-message">Loading {groupType.toLowerCase()} data...</p>
                  ) : (
                    <>
                      <div className="client-data-table">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Invoice</th>
                              <th>Invoice Date (2)</th>
                              <th>Client PO Number</th>
                              <th>Model</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupData.map((item) => (
                              <tr key={item.id}>
                                <td>{item['Date'] || 'N/A'}</td>
                                <td>{item['Invoice'] || 'N/A'}</td>
                                <td>{item['Invoice Date (2)'] || 'N/A'}</td>
                                <td>{item['Client PO Number'] || 'N/A'}</td>
                                <td>{item['Model'] || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {hasMoreData && (
                        <button
                          className="load-more-btn"
                          onClick={loadMoreData}
                          disabled={isLoadingGroupData}
                        >
                          {isLoadingGroupData ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="no-client-selected">
                  <p>Select a {groupType.toLowerCase()} to view their details</p>
                </div>
              )}
            </div>
          </div>

          {/* CSV Column Selection Modal */}
          {showCSVModal && (
            <div className="modal-overlay">
              <div className="modal-content" style={{ maxWidth: 400 }}>
                <div className="modal-header">
                  <h2>Select Columns to Download</h2>
                  <button className="modal-close" onClick={handleCloseCSVModal}>&times;</button>
                </div>
                <form onSubmit={handleSubmitCSVModal}>
                  <div className="modal-body" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <div className="csv-modal-btn-row">
                      <button type="button" className="csv-modal-btn csv-modal-btn-select" onClick={handleSelectAllColumns}>Select All</button>
                      <button type="button" className="csv-modal-btn csv-modal-btn-deselect" onClick={handleDeselectAllColumns}>Deselect All</button>
                    </div>
                    {allColumns.length === 0 && <p>No columns available.</p>}
                    {allColumns.map(col => (
                      <div key={col} style={{ marginBottom: 6 }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(col)}
                            onChange={() => handleColumnCheckbox(col)}
                          />
                          {col}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="modal-cancel" onClick={handleCloseCSVModal}>Cancel</button>
                    <button type="submit" className="modal-save" style={{ marginLeft: 8 }}>Download</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Groups; 